import React, { useState, useEffect } from 'react';
import { MsalProvider, useMsal, UnauthenticatedTemplate, AuthenticatedTemplate } from '@azure/msal-react';
import { PublicClientApplication, InteractionStatus } from '@azure/msal-browser';
import { msalConfig, loginRequest, apiTokenRequest } from './config/auth';
import { MessageFeed } from './components/MessageFeed';
import AIAssist from './components/AIAssist';
import ErrorReportsInbox from './components/ErrorReportsInbox';
import ApiInteractions from './components/ApiInteractions';
import AIInfo from './components/AIInfo';
import Toast from './components/Toast';
import GlobalLoadingBar from './components/GlobalLoadingBar';
import { setCurrentUser, setAuthToken, setTokenProvider, isAdmin, getErrorReports } from './services/api';
import { registerMsal } from './services/graph';
import { LogOut, MessageCircle, Activity, Brain, Sparkles, ClipboardList } from 'lucide-react';
import NotificationBell from './components/NotificationBell';
import { useSignalR } from './hooks/useSignalR';

const msalInstance = new PublicClientApplication(msalConfig);
// Hand the shared MSAL instance to the Graph directory helper (no second
// instance). getOrgUsers() reads the active account off this lazily; until the
// app-registration grants User.ReadBasic.All + admin consent, it safely no-ops.
registerMsal(msalInstance);

// Give the API client an on-demand token source. The axios request interceptor
// calls this before EVERY call so the attached bearer is always current, and the
// 401 interceptor calls it with forceRefresh to recover in place. acquireTokenSilent
// returns the cached token instantly when valid and silently renews (via the SPA
// refresh token) near/at expiry — so Azure's access-token rollover (whatever the
// tenant's lifetime, e.g. ~20 min) never surfaces as a "sign-in expired" toast.
// Returns null on failure (no active account / dead refresh token / interaction
// required); the 401 path then shows the prompt. We do NOT redirect here — that
// stays owned by MainApp's consent-guarded token effect to avoid redirect loops.
setTokenProvider(async (opts) => {
  const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
  if (!account) return null;
  try {
    const res = await msalInstance.acquireTokenSilent({
      ...apiTokenRequest,
      account,
      forceRefresh: !!opts?.forceRefresh,
    });
    return res?.accessToken || null;
  } catch {
    return null;
  }
});

// A redirect that never returns (e.g. AAD renders an error page on its own
// domain) leaves MSAL's "interaction.status" flag stuck in storage. On the next
// load handleRedirectPromise won't clear it (there's no response hash to
// process), so the flag orphans and every loginRedirect throws
// interaction_in_progress — even though React's inProgress reads None. Clear the
// orphan on a fresh (non-redirect) load. We only do this when the URL carries NO
// auth response, so a genuine redirect return is never disturbed.
function clearStaleMsalInteraction(): void {
  const params = window.location.hash + window.location.search;
  const isRedirectResponse =
    params.includes('code=') || params.includes('error=') || params.includes('id_token=') || params.includes('state=');
  if (isRedirectResponse) return;
  try {
    for (const store of [window.sessionStorage, window.localStorage]) {
      Object.keys(store)
        .filter((k) => k.includes('interaction.status'))
        .forEach((k) => store.removeItem(k));
    }
  } catch {
    /* storage may be unavailable (private mode) — nothing to clear */
  }
}

function LoginPage() {
  const { instance, inProgress } = useMsal();

  // Gate the click on MSAL's real interaction state, NOT a local flag. Calling
  // loginRedirect while inProgress !== None throws BrowserAuthError
  // "interaction_in_progress" (e.g. a redirect is still being processed, or a
  // stale interaction flag lingered from a prior failed redirect). Only fire
  // when MSAL is idle; the button also disables until then.
  const busy = inProgress !== InteractionStatus.None;

  const handleLogin = () => {
    if (busy) return;
    instance.loginRedirect(loginRequest).catch(e => {
      console.error('Login error:', e);
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="bg-white p-8 rounded-lg shadow-lg text-center">
        <h1 className="text-3xl font-bold text-brand-700 mb-2">SalesLogix</h1>
        <p className="text-gray-600 mb-6">Sales Communication Command Center</p>
        <button
          onClick={handleLogin}
          disabled={busy}
          className="bg-brand-700 text-white px-6 py-3 rounded-lg hover:bg-brand-800 transition-colors disabled:opacity-50"
        >
          {busy ? 'Signing in...' : 'Sign in with Microsoft'}
        </button>
      </div>
    </div>
  );
}

function MainApp() {
  const { instance, accounts } = useMsal();
  // SignalR lives HERE (one connection for the whole app) so the header "LIVE"
  // pill reflects the REAL real-time connection state, and lastMessage is passed
  // down to the feed. The pill is green LIVE only when actually connected.
  const { connectionState, lastMessage } = useSignalR();
  const rtLive = connectionState === 'connected';
  const rtReconnecting = connectionState === 'reconnecting';
  // AI Assist chat panel (replaces the old Team Chat). Same right-aside slot.
  const [showAssist, setShowAssist] = useState(false);
  // One-shot launcher payload for AIAssist: when a rep clicks "Troubleshoot this
  // thread" on a ConversationCard, or an admin clicks "continue investigation in
  // chat" in the Error Reports inbox, a window 'sl-open-assist' event carries a
  // threadRef (+ optional seed message). We open the panel and pass it to
  // AIAssist keyed on a nonce so the SAME thread can be re-launched repeatedly.
  const [assistLaunch, setAssistLaunch] = useState<{ threadRef: number | null; message?: string; greet?: boolean; nonce: number } | null>(null);
  // Admin-only API Interaction Tracking dashboard toggle. The toggle button is
  // rendered only when isAdmin() is true (UX gate; the backend enforces ADMIN).
  const [showApiInteractions, setShowApiInteractions] = useState(false);
  // Admin-only "AI Info" curation surface toggle (browse/edit/archive the AI's
  // learned knowledge). Same UX-gate + backend-enforced pattern as above.
  const [showAIInfo, setShowAIInfo] = useState(false);
  // Admin-only Error Reports inbox toggle + its open-report badge count. Same
  // UX-gate + backend-enforced pattern; the count polls GET /error-reports?
  // status=open on the established GET-poll precedent (no SignalR).
  const [showErrorReports, setShowErrorReports] = useState(false);
  const [openReportCount, setOpenReportCount] = useState(0);
  // Admin flag as REACTIVE state. isAdmin() reads a module-level variable that is
  // populated by setCurrentUser() in the effect below; mutating that plain
  // variable does NOT trigger a React re-render, so on first paint the roles were
  // still empty and the admin toggles stayed hidden until an unrelated state
  // change (e.g. opening Team Chat) forced a re-render. Mirror it into state,
  // updated in the same effect, so the admin icons appear immediately at login.
  const [isAdminUser, setIsAdminUser] = useState<boolean>(isAdmin());

  const handleLogout = () => {
    instance.logoutRedirect().catch(e => console.error(e));
  };

  const user = accounts[0];

  // Make the signed-in user available to API calls (manual reply attribution,
  // comments) so outbound messages are stamped with who actually sent them.
  useEffect(() => {
    const roles = (user?.idTokenClaims as any)?.roles as string[] | undefined;
    setCurrentUser(user?.name, user?.username, roles);
    // Re-evaluate admin status now that roles are set — this state update is what
    // re-renders the header so the admin toggles show without any user action.
    setIsAdminUser(isAdmin());
  }, [user?.name, user?.username, user?.idTokenClaims]);

  // Acquire an access token for our API and attach it so the backend can verify
  // identity + role. Silent; re-acquires periodically (tokens ~60-90 min). If a
  // one-time consent is needed, redirect once (guarded to avoid a loop).
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const getToken = async () => {
      try {
        const res = await instance.acquireTokenSilent({ ...apiTokenRequest, account: user });
        if (!cancelled && res?.accessToken) setAuthToken(res.accessToken);
      } catch (e) {
        // Any silent failure (consent needed, no cached token, etc.) -> one
        // interactive redirect to get consent + token. Guarded against loops.
        if (!sessionStorage.getItem('apiConsentTried')) {
          sessionStorage.setItem('apiConsentTried', '1');
          try { await instance.acquireTokenRedirect(apiTokenRequest); } catch (e2) {
            console.warn('Interactive token acquisition failed:', e2);
          }
          return;
        }
        console.warn('API token acquisition failed (continuing without it):', e);
      }
    };
    getToken();
    const id = setInterval(getToken, 45 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [instance, user]);

  // Launch AIAssist from anywhere in the app via a window 'sl-open-assist' event
  // (same decoupled launcher pattern the feed uses for 'sl-focus-convo'). The
  // detail carries { threadRef, message? }; we open the panel and stash the
  // payload with a bumped nonce so re-launching the SAME thread still re-seeds.
  useEffect(() => {
    const handler = (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      setAssistLaunch({
        threadRef: typeof d.threadRef === 'number' ? d.threadRef : null,
        message: typeof d.message === 'string' ? d.message : undefined,
        greet: d.greet === true,
        nonce: Date.now(),
      });
      setShowAssist(true);
    };
    window.addEventListener('sl-open-assist', handler);
    return () => window.removeEventListener('sl-open-assist', handler);
  }, []);

  // Broadcast the assist panel's open/closed state so components rendered in
  // portals (e.g. ThreadModal on document.body, outside this subtree) can lay
  // themselves out beside the panel — the modal shifts left to make room, and
  // its floating "AI Assist" launcher hides once the panel is open.
  useEffect(() => {
    (window as any).__slAssistOpen = showAssist;
    window.dispatchEvent(new CustomEvent('sl-assist-open-changed', { detail: { open: showAssist } }));
  }, [showAssist]);

  // Admin-only open-error-report badge. Polls GET /error-reports?status=open on
  // the established GET-poll precedent (10s; no SignalR). Gated on isAdminUser so a
  // non-admin never fires the admin-scoped request. Non-fatal: a failure leaves
  // the last count in place.
  useEffect(() => {
    if (!isAdminUser) { setOpenReportCount(0); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const { count } = await getErrorReports({ status: 'open' });
        if (!cancelled) setOpenReportCount(count || 0);
      } catch (e) {
        /* non-fatal: keep the last known count */
      }
    };
    poll();
    const id = setInterval(poll, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isAdminUser]);

  return (
    // overflow-x-CLIP (not hidden): clips horizontal overflow WITHOUT forcing
    // overflow-y to auto, which would create a scroll container that breaks
    // position:sticky for the app header and the feed's sticky filter bar.
    <div className="min-h-screen bg-gray-50 max-w-full overflow-x-clip">
      <Toast />
      <GlobalLoadingBar />
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-brand-700 rounded-lg flex items-center justify-center mr-2">
                  <MessageCircle className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-brand-700">SalesLogix</span>
              </div>
              <span
                title={`Real-time connection: ${connectionState}`}
                className={"ml-2 sm:ml-4 px-2 py-1 text-xs font-medium rounded-full flex items-center " + (
                  rtLive ? "bg-green-100 text-green-700"
                  : rtReconnecting ? "bg-amber-100 text-amber-700"
                  : "bg-gray-200 text-gray-600")}
              >
                <span className={"w-2 h-2 rounded-full mr-1 " + (
                  rtLive ? "bg-green-500 animate-pulse"
                  : rtReconnecting ? "bg-amber-500 animate-pulse"
                  : "bg-gray-400")}></span>
                {rtLive ? "LIVE" : rtReconnecting ? "Reconnecting" : "Offline"}
              </span>
            </div>

            {/* Right side */}
            <div className="flex items-center space-x-1 sm:space-x-4">
              {/* Notifications */}
              <NotificationBell />

              {/* AI Assist Toggle (replaces Team Chat; same right-aside slot). A
                  manual open starts a fresh chat — clear any stale launcher
                  payload so it isn't re-attached to an old thread. */}
              <button
                data-assist-toggle
                onClick={() => { if (!showAssist) setAssistLaunch(null); setShowAssist(!showAssist); }}
                title="Sally's Assist Chat"
                className={showAssist ? "p-2 rounded-lg transition-colors bg-brand-100 text-brand-700" : "p-2 rounded-lg transition-colors text-gray-500 hover:text-brand-700"}
              >
                <Sparkles className="w-5 h-5" />
              </button>

              {/* Error Reports Toggle (admin-only; backend enforces ADMIN). Badge
                  shows the open-report count from the poll above. Opening it closes
                  the other admin panes (mirrors the API Interactions / AI Info
                  mutual-exclusion). */}
              {isAdminUser && (
                <button
                  onClick={() => { setShowErrorReports(!showErrorReports); setShowApiInteractions(false); setShowAIInfo(false); }}
                  title="Error reports"
                  className={"relative " + (showErrorReports ? "p-2 rounded-lg transition-colors bg-brand-100 text-brand-700" : "p-2 rounded-lg transition-colors text-gray-500 hover:text-brand-700")}
                >
                  <ClipboardList className="w-5 h-5" />
                  {openReportCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[1.1rem] h-[1.1rem] px-1 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {openReportCount > 9 ? '9+' : openReportCount}
                    </span>
                  )}
                </button>
              )}

              {/* API Interactions Toggle (admin-only; backend enforces ADMIN).
                  Mirrors the Team Chat toggle's boolean-state pattern. */}
              {isAdminUser && (
                <button
                  onClick={() => { setShowApiInteractions(!showApiInteractions); setShowAIInfo(false); setShowErrorReports(false); }}
                  title="API interactions"
                  className={showApiInteractions ? "p-2 rounded-lg transition-colors bg-brand-100 text-brand-700" : "p-2 rounded-lg transition-colors text-gray-500 hover:text-brand-700"}
                >
                  <Activity className="w-5 h-5" />
                </button>
              )}

              {/* AI Info Toggle (admin-only; backend enforces ADMIN). Mirrors the
                  API Interactions toggle; opening one admin pane closes the other. */}
              {isAdminUser && (
                <button
                  onClick={() => { setShowAIInfo(!showAIInfo); setShowApiInteractions(false); setShowErrorReports(false); }}
                  title="AI Info"
                  className={showAIInfo ? "p-2 rounded-lg transition-colors bg-brand-100 text-brand-700" : "p-2 rounded-lg transition-colors text-gray-500 hover:text-brand-700"}
                >
                  <Brain className="w-5 h-5" />
                </button>
              )}

              {/* User Menu */}
              <div className="flex items-center">
                <span className="hidden sm:inline text-sm text-gray-700 mr-3 truncate max-w-[160px]">{user?.name || user?.username}</span>
                <button 
                  onClick={handleLogout}
                  className="p-2 text-gray-500 hover:text-red-600 transition-colors"
                  title="Sign out"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex">
        {/* Main pane: the API Interactions dashboard when an admin has it toggled
            on (it replaces the feed view), otherwise the Message Feed. The admin
            gate lives on both the toggle button and inside ApiInteractions. */}
        <main className={showAssist ? "flex-1 min-w-0 transition-all duration-300 sm:mr-[30rem]" : "flex-1 min-w-0 transition-all duration-300"}>
          {showErrorReports && isAdminUser ? <ErrorReportsInbox /> : showAIInfo && isAdminUser ? <AIInfo /> : showApiInteractions && isAdminUser ? <ApiInteractions /> : <MessageFeed lastMessage={lastMessage} />}
        </main>

        {/* AI Assist Sidebar (full-width overlay on mobile, fixed panel on
            desktop) — replaces the old Team Chat in the same slot. Keyed on the
            launcher nonce so a "Troubleshoot this thread" / "continue in chat"
            launch remounts AIAssist with the new threadRef + seed message. */}
        {showAssist && (
          <aside className="fixed right-0 top-16 bottom-0 w-full sm:w-[30rem] bg-white border-l border-gray-200 shadow-xl z-40">
            <AIAssist
              key={assistLaunch?.nonce ?? 'assist'}
              onClose={() => setShowAssist(false)}
              initialThreadRef={assistLaunch?.threadRef ?? null}
              initialMessage={assistLaunch?.message}
              initialAutoGreet={assistLaunch?.greet ?? false}
            />
          </aside>
        )}
      </div>
    </div>
  );
}

function App() {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    clearStaleMsalInteraction();
    msalInstance.initialize().then(() => {
      msalInstance.handleRedirectPromise().then(async (response) => {
        if (response && response.account) {
          msalInstance.setActiveAccount(response.account);
        }
        // Do NOT trust response.accessToken as the API bearer. loginRequest spans
        // TWO resources (Microsoft Graph + our api://… scope), and AAD issues a
        // token for only ONE resource per request — the login redirect's token is
        // a GRAPH token, not an API token. Setting it as the API bearer makes the
        // first API calls 401 ("sign-in expired") until MainApp's effect quietly
        // re-acquires the right one. Instead, acquire the API token for its scope
        // specifically, BEFORE any component mounts and fires a request, so the
        // correct bearer is in place from the first call.
        const account =
          msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
        if (account) {
          try {
            const res = await msalInstance.acquireTokenSilent({ ...apiTokenRequest, account });
            if (res?.accessToken) setAuthToken(res.accessToken);
          } catch (e) {
            // Silent acquisition can fail (needs consent, no cached token). Leave
            // it to MainApp's token effect, which redirects once for consent.
            console.warn('Initial API token acquisition failed (deferring to MainApp):', e);
          }
        }
        setIsReady(true);
      }).catch((error) => {
        console.error('Redirect error:', error);
        setIsReady(true);
      });
    });
  }, []);

  if (!isReady) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <MsalProvider instance={msalInstance}>
      <AuthenticatedTemplate>
        <MainApp />
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <LoginPage />
      </UnauthenticatedTemplate>
    </MsalProvider>
  );
}

export default App;
